import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Activity, Shield, Box, Zap, Database, Terminal, Radio } from 'lucide-react';
import { MathService } from '../services/mathService';

export const SovereignWitness: React.FC = () => {
  const [logs, setLogs] = useState<{ id: string; msg: string; type: 'gate1' | 'gate2' | 'gate3' | 'full' | 'broadcast' | 'firewall' | 'heal' | 'mbr' | 'audit' | 'system' }[]>([]);
  const [activeResonance, setActiveResonance] = useState(0);
  const [stats, setStats] = useState({
    interactions: 1_000_000_000,
    depth: "10⁻¹²",
    reclaimed: "0.000 UNIT",
    sinkStatus: "STABLE",
    drift: "0.000%"
  });
  const [fullCircleData, setFullCircleData] = useState<any>(null);
  const [firewallStatus, setFirewallStatus] = useState<string>("ABSOLUTE");
  const [healingStatus, setHealingStatus] = useState<string>("SYNCED");
  const [mbrData, setMbrData] = useState<any>(null);
  const [reclaimedTotal, setReclaimedTotal] = useState<number>(0);

  const addLog = (msg: string, type: 'gate1' | 'gate2' | 'gate3' | 'full' | 'broadcast' | 'firewall' | 'heal' | 'mbr' | 'audit' | 'system') => {
    setLogs(prev => [{ id: Math.random().toString(), msg, type }, ...prev].slice(0, 50));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const actions = ['gate1', 'gate2', 'gate3', 'full', 'broadcast', 'firewall', 'heal', 'mbr', 'audit'];
      const actionType = actions[Math.floor(Math.random() * actions.length)] as any;
      
      if (actionType === 'gate1') {
        const result = MathService.gate1Interaction();
        addLog(`GATE1: Substrate Initialized -> ${result.substrate}`, 'gate1');
        setActiveResonance(8.27);
      } else if (actionType === 'gate2') {
        const result = MathService.gate2Anchor();
        addLog(`GATE2: Anchor Synchronized -> ${result.anchor}`, 'gate2');
        setActiveResonance(14350);
      } else if (actionType === 'gate3') {
        const result = MathService.gate3Expansion();
        addLog(`GATE3: Structural Expansion -> ${result.status}`, 'gate3');
        setActiveResonance(result.expansion_constant);
      } else if (actionType === 'full') {
        const result = MathService.fullCircleSovereignty();
        setFullCircleData(result);
        addLog(`MANIFEST: Full-Circle Sovereignty -> ${result.status}`, 'full');
        setActiveResonance(8.27);
      } else if (actionType === 'broadcast') {
        const result = MathService.quintessenceTransmit(`PULSE_${Math.floor(Math.random() * 1000)}`);
        addLog(`TRANSMIT: Quintessence Watermark -> ${result.data}`, 'broadcast');
        setActiveResonance(8.27);
      } else if (actionType === 'firewall') {
        const result = MathService.solidStateFirewall(`PAYOUT_${Math.floor(Math.random() * 5000)}`);
        setFirewallStatus(result.status);
        addLog(`FIREWALL: Physical Bridge Secured -> ${result.bridge}`, 'firewall');
        setActiveResonance(14350);
      } else if (actionType === 'heal') {
        const isNoise = Math.random() > 0.7;
        const packet = isNoise ? "CORRUPT_1.0_ROUNDING_ERROR" : "STABLE_PULSE_774CE";
        const result = MathService.sovereignSelfHeal(packet);
        setHealingStatus(result.status);
        addLog(`HEALER: ${result.status === 'HEALED' ? 'DRIFT_DETECTED' : 'STREAM_STABLE'} -> ${result.result}`, 'heal');
        setActiveResonance(8.27);
      } else if (actionType === 'mbr') {
        const result = MathService.finalizeLatticeMBR();
        setMbrData(result);
        addLog(`MBR: Lattice Finalized -> ${result.protocol} PROTOCOL ACTIVE`, 'mbr');
        setActiveResonance(result.resonanceSync);
      } else if (actionType === 'audit') {
        const pool = Array.from({ length: 5 }, () => 0);
        const result = MathService.integrityAuditTrace(pool);
        setReclaimedTotal(prev => prev + parseFloat(result.reclaimed));
        addLog(`AUDIT: Reclaiming Void -> ${result.note}`, 'audit');
        setActiveResonance(8.27);
      }

      // Random system pulse
      if (Math.random() > 0.8) {
        addLog("SYSTEM: 11B NODE HANDSHAKE VERIFIED.", 'system');
      }

    setStats(prev => ({
      ...prev,
      interactions: prev.interactions + Math.floor(Math.random() * 1000),
      reclaimed: `${reclaimedTotal.toFixed(3)} UNIT`,
      drift: (Math.random() * 0.001).toFixed(3) + "%"
    }));
  }, 2000);

    return () => clearInterval(interval);
  }, [stats.interactions]);

  return (
    <div className="glass-panel p-8 border-rose-500/10 bg-black/60 relative overflow-hidden h-[700px] flex flex-col">
      <div className="absolute top-0 right-0 p-8 opacity-10">
        <Radio className="w-48 h-48 text-rose-500 animate-pulse" />
      </div>

      <div className="flex items-center justify-between mb-8 relative z-10">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-rose-500/10 text-rose-400">
              <Activity className="w-6 h-6 animate-pulse" />
            </div>
            <h3 className="text-3xl font-display font-bold text-white uppercase tracking-tighter italic">Sovereign <span className="text-rose-400">Witness</span></h3>
          </div>
          <p className="text-[10px] font-mono text-rose-400/60 uppercase tracking-[0.4em]">Autonomous Trillion-Scale Manifest [1x1=2]</p>
        </div>

        <div className="flex gap-4">
          <div className="px-4 py-2 bg-rose-500/10 border border-rose-500/20 rounded-xl flex flex-col items-end">
            <span className="text-[8px] font-mono text-rose-400/40 uppercase">Resonance</span>
            <span className="text-sm font-mono text-white font-bold">{activeResonance === 14350 ? 'ANCHOR_SYNC' : activeResonance.toFixed(2)}</span>
          </div>
          <div className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex flex-col items-end">
            <span className="text-[8px] font-mono text-emerald-400/40 uppercase">Drift</span>
            <span className="text-sm font-mono text-white font-bold">0.000000%</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-8 gap-4 mb-8 relative z-10">
        <div className="p-4 bg-white/5 border border-white/10 rounded-2xl flex flex-col gap-1">
          <span className="text-[8px] font-mono text-white/40 uppercase">Total Interactions</span>
          <span className="text-lg font-mono text-white font-bold tracking-tighter">{stats.interactions.toLocaleString()}</span>
        </div>
        <div className="p-4 bg-white/5 border border-white/10 rounded-2xl flex flex-col gap-1">
          <span className="text-[8px] font-mono text-white/40 uppercase">Witness Scale</span>
          <span className="text-lg font-mono text-rose-400 font-bold tracking-tighter">10⁻¹²,⁰⁰⁰,⁰⁰⁰,⁰⁰⁰,⁰⁰⁰</span>
        </div>
        <div className="p-4 bg-white/5 border border-white/10 rounded-2xl flex flex-col gap-1">
          <span className="text-[8px] font-mono text-white/40 uppercase">Reclaimed Value</span>
          <span className="text-lg font-mono text-blue-400 font-bold tracking-tighter">{stats.reclaimed}</span>
        </div>
        <div className="p-4 bg-white/5 border border-white/10 rounded-2xl flex flex-col gap-1">
          <span className="text-[8px] font-mono text-white/40 uppercase">Sink Address</span>
          <span className="text-lg font-mono text-lattice-cyan font-bold truncate tracking-tighter">3tt1L...VvZh</span>
        </div>
        <div className="p-4 bg-white/5 border border-white/10 rounded-2xl flex flex-col gap-1">
          <span className="text-[8px] font-mono text-white/40 uppercase">Operating Path</span>
          <span className="text-lg font-mono text-emerald-400 font-bold tracking-tighter">ZERO_DRIFT</span>
        </div>
        <div className="p-4 bg-orange-500/5 border border-orange-500/20 rounded-2xl flex flex-col gap-1">
          <span className="text-[8px] font-mono text-orange-400/60 uppercase">Firewall Status</span>
          <span className="text-lg font-mono text-orange-400 font-bold tracking-tighter">{firewallStatus}</span>
        </div>
        <div className="p-4 bg-lattice-cyan/5 border border-lattice-cyan/20 rounded-2xl flex flex-col gap-1">
          <span className="text-[8px] font-mono text-lattice-cyan uppercase">Healer Status</span>
          <span className="text-lg font-mono text-lattice-white font-bold tracking-tighter">{healingStatus}</span>
        </div>
        <div className="p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-2xl flex flex-col gap-1">
          <span className="text-[8px] font-mono text-yellow-400 uppercase">MBR Protocol</span>
          <span className="text-lg font-mono text-yellow-500 font-bold tracking-tighter">{mbrData?.protocol || "INIT..."}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 flex-1 min-h-0 relative z-10">
        {/* Full Circle Visualization */}
        <div className="md:col-span-4 bg-black/40 border border-rose-500/10 rounded-2xl p-6 flex flex-col gap-6">
          <div className="flex items-center gap-2">
            <Box className="w-4 h-4 text-rose-500" />
            <span className="text-[10px] font-mono text-rose-400 uppercase font-bold tracking-widest">Full-Circle Split</span>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 bg-white/5 rounded-xl border border-white/10">
              <div className="flex justify-between items-center mb-1">
                <span className="text-[9px] font-mono text-white/40 uppercase">Humanitarian (45%)</span>
                <span className="text-[10px] font-mono text-rose-400 font-bold">LOCKED</span>
              </div>
              <div className="text-sm font-mono text-white font-bold truncate">
                {fullCircleData?.humanitarian || "WAITING_FOR_PULSE..."}
              </div>
            </div>

            <div className="p-4 bg-white/5 rounded-xl border border-white/10">
              <div className="flex justify-between items-center mb-1">
                <span className="text-[9px] font-mono text-white/40 uppercase">Infrastructure (55%)</span>
                <span className="text-[10px] font-mono text-emerald-400 font-bold">LOCKED</span>
              </div>
              <div className="text-sm font-mono text-white font-bold truncate">
                {fullCircleData?.infrastructure || "WAITING_FOR_PULSE..."}
              </div>
            </div>

            <div className="p-4 bg-rose-500/5 rounded-xl border border-rose-500/20">
              <div className="flex justify-between items-center mb-1">
                <span className="text-[9px] font-mono text-rose-400/60 uppercase">Amalgamation_Mind</span>
              </div>
              <div className="text-[10px] font-mono text-white leading-relaxed break-all">
                {fullCircleData?.mindState || "14350_BP_SOURCE"}
              </div>
            </div>
          </div>
        </div>

        {/* Live Logs */}
        <div className="md:col-span-8 bg-black/40 border border-white/10 rounded-2xl p-6 overflow-hidden relative group">
          <div className="absolute top-4 right-4 flex items-center gap-2 opacity-40">
             <Terminal className="w-3 h-3 text-rose-400" />
             <span className="text-[8px] font-mono text-rose-400 uppercase tracking-widest">Autonomous Witness Log</span>
          </div>

          <div className="h-full overflow-y-auto pr-4 scrollbar-hide space-y-2 font-mono text-[11px]">
            <AnimatePresence mode="popLayout">
              {logs.map((log) => (
                <motion.div
                  key={log.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className={`py-1.5 px-3 rounded flex items-center gap-3 border-l-2 transition-all ${
                    log.type === 'gate1' ? 'bg-rose-500/5 border-rose-500 text-rose-200' :
                    log.type === 'gate2' ? 'bg-purple-500/5 border-purple-500 text-purple-200' :
                    log.type === 'gate3' ? 'bg-emerald-500/5 border-emerald-500 text-emerald-200' :
                    log.type === 'full' ? 'bg-lattice-cyan/10 border-lattice-cyan text-lattice-white shadow-[0_0_10px_rgba(34,211,238,0.1)]' :
                    log.type === 'broadcast' ? 'bg-orange-500/10 border-orange-500 text-orange-200 shadow-[0_0_15px_rgba(249,115,22,0.15)]' :
                    log.type === 'firewall' ? 'bg-rose-500/10 border-rose-500 text-rose-200 shadow-[0_0_15px_rgba(244,63,94,0.15)]' :
                    log.type === 'heal' ? 'bg-lattice-cyan/10 border-lattice-cyan text-lattice-white shadow-[0_0_15px_rgba(34,211,238,0.2)]' :
                    log.type === 'mbr' ? 'bg-yellow-500/10 border-yellow-500 text-yellow-200 shadow-[0_0_20px_rgba(234,179,8,0.25)] ring-1 ring-yellow-500/50' :
                    log.type === 'audit' ? 'bg-blue-500/10 border-blue-500 text-blue-200 shadow-[0_0_15px_rgba(59,130,246,0.2)]' :
                    'bg-white/5 border-white/20 text-white/60'
                  }`}
                >
                  <div className={`w-1.5 h-1.5 rounded-full bg-current ${log.type === 'full' || log.type === 'broadcast' || log.type === 'firewall' || log.type === 'heal' || log.type === 'mbr' || log.type === 'audit' ? 'animate-bounce' : 'animate-pulse'} shrink-0`} />
                  <span className="opacity-40 shrink-0">[{new Date().toLocaleTimeString()}]</span>
                  <span className="font-bold shrink-0">{log.type.toUpperCase()}</span>
                  <span className="truncate">{log.msg}</span>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between text-[8px] font-mono text-white/20 uppercase tracking-[0.2em] relative z-10">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
             <Box className="w-3 h-3" /> INFRASTRUCTURE_SYNC_001
          </div>
          <div className="flex items-center gap-1">
             <Database className="w-3 h-3" /> TREASURY_RECLAMATION_ACTIVE
          </div>
          <div className="flex items-center gap-1">
             <Zap className="w-3 h-3" /> Ω_FLOW_ON
          </div>
        </div>
        <div>
          Witness ID: 14350_BP_SOURCE_ANALYST
        </div>
      </div>

      {/* Aesthetic grid overlay */}
      <div className="absolute inset-0 pointer-events-none grid-bg opacity-10" />
    </div>
  );
};
